import { ActivityStep, StopStatus, TripActivityStatus } from "@cnx/common";
import {
  IconBed,
  IconCheck,
  IconHandStop,
  IconInfoCircle,
  IconTool,
  IconTruckDelivery,
  IconTruckReturn,
  IconWorldCheck,
} from "@tabler/icons-react";
import theme from "common/theme";
import IconRoundedCamera from "icons/common/rounded-camera.svg";
import IconCoupling from "icons/shipment/coupling.svg";
import IconDecoupling from "icons/shipment/decoupling.svg";
import IconDividedTruck from "icons/shipment/divided-truck.svg";
import IconFromShip from "icons/shipment/from-ship.svg";
import IconFromTrain from "icons/shipment/from-train.svg";
import IconLoadTruck from "icons/shipment/load-truck.svg";
import IconMissing from "icons/shipment/missing.svg";
import IconToShip from "icons/shipment/to-ship.svg";
import IconToTrain from "icons/shipment/to-train.svg";

const statusToIconMap: Record<ActivityStep, React.ReactElement> = {
  [ActivityStep.LOADING]: (
    <IconLoadTruck width={24} height={24} className="text-green" />
  ),
  [ActivityStep.UNLOADING]: (
    <IconTruckDelivery width={24} height={24} className="text-red-dark" />
  ),
  [ActivityStep.STOPPED]: <IconHandStop className="text-red" />,
  [ActivityStep.COUPLING]: (
    <IconCoupling width={24} height={24} className="text-yellow-dark" />
  ),
  [ActivityStep.DECOUPLING]: (
    <IconDecoupling width={24} height={24} className="text-gray-dark" />
  ),
  [ActivityStep.DIVIDER]: (
    <IconDividedTruck width={24} height={24} className="test-blue" />
  ),
  [ActivityStep.DIVIDER_HARD]: <IconDividedTruck width={24} height={24} />,
  [ActivityStep.TO_SHIP]: (
    <IconToShip width={24} height={24} className="text-green-dark" />
  ),
  [ActivityStep.FROM_SHIP]: (
    <IconFromShip className="text-red-dark" width={24} height={24} />
  ),
  [ActivityStep.TO_TRAIN]: <IconToTrain width={24} height={24} />,
  [ActivityStep.FROM_TRAIN]: <IconFromTrain width={24} height={24} />,
  [ActivityStep.CUSTOMS]: <IconWorldCheck className="text-yellow-dark" />,
  [ActivityStep.MAINTENANCE]: <IconTool className="text-blue" />,
  [ActivityStep.REST]: <IconBed className="text-blue" />,
  [ActivityStep.RETURNED]: <IconTruckReturn className="text-yellow-dark" />,
  [ActivityStep.INFO]: (
    <IconInfoCircle width={24} height={24} className="text-gray-dark" />
  ),
  [ActivityStep.DRIVER_CHANGE]: (
    <IconInfoCircle width={24} height={24} className="text-gray-dark" />
  ),
  [ActivityStep.UNCLEAR]: (
    <IconMissing width={24} height={24} className="text-yellow-dark" />
  ),
};

const statusToBackgroundColorMap: Record<ActivityStep, string | undefined> = {
  [ActivityStep.LOADING]: theme.colors?.green?.[4],
  [ActivityStep.UNLOADING]: theme.colors?.red?.[8],
  [ActivityStep.STOPPED]: theme.colors?.red?.[4],
  [ActivityStep.COUPLING]: theme.colors?.yellow?.[8],
  [ActivityStep.DECOUPLING]: theme.colors?.gray?.[8],
  [ActivityStep.DIVIDER]: theme.colors?.blue?.[4],
  [ActivityStep.DIVIDER_HARD]: theme.colors?.gray?.[4],
  [ActivityStep.TO_SHIP]: theme.colors?.green?.[8],
  [ActivityStep.FROM_SHIP]: theme.colors?.red?.[8],
  [ActivityStep.TO_TRAIN]: theme.colors?.green?.[8],
  [ActivityStep.FROM_TRAIN]: theme.colors?.red?.[8],
  [ActivityStep.CUSTOMS]: theme.colors?.yellow?.[8],
  [ActivityStep.MAINTENANCE]: theme.colors?.blue?.[4],
  [ActivityStep.REST]: theme.colors?.blue?.[4],
  [ActivityStep.RETURNED]: theme.colors?.yellow?.[8],
  [ActivityStep.INFO]: theme.colors?.gray?.[4],
  [ActivityStep.DRIVER_CHANGE]: theme.colors?.gray?.[4],
  [ActivityStep.UNCLEAR]: "#E2B216",
};

type ActivityStatusTitle = "markAsDone" | "done" | "uploadCMR";

type ActivityStatusOption = {
  title: ActivityStatusTitle;
  icon: React.ReactElement;
  status: TripActivityStatus;
  update: {
    status: TripActivityStatus;
  } | null;
};

const activityStatusOptions: ActivityStatusOption[] = [
  {
    title: "markAsDone",
    icon: <IconCheck />,
    status: TripActivityStatus.NOT_DONE,
    update: { status: TripActivityStatus.DONE },
  },
  {
    title: "done",
    icon: <IconCheck className="text-green" />,
    status: TripActivityStatus.DONE,
    update: null,
  },
  {
    title: "uploadCMR",
    icon: (
      <IconRoundedCamera
        strokeWidth={2}
        className="text-white"
        width={24}
        height={24}
      />
    ),
    status: TripActivityStatus.UNLOADED,
    update: { status: TripActivityStatus.DONE },
  },
  {
    title: "markAsDone",
    icon: <IconCheck />,
    status: TripActivityStatus.UNCLEAR,
    update: { status: TripActivityStatus.DONE },
  },
];

export const selectActivityStatusOption = ({
  isAlreadyHaveCMR,
  status,
}: {
  status: TripActivityStatus;
  isAlreadyHaveCMR: boolean;
}) => {
  if (status === TripActivityStatus.UNLOADED && isAlreadyHaveCMR) {
    return activityStatusOptions[0];
  }
  return activityStatusOptions[status];
};

export const selectIconByStatus = (status: ActivityStep) =>
  statusToIconMap[status] || <></>;

export const selectBackgroundColorByStatus = (status: ActivityStep) =>
  statusToBackgroundColorMap[status] ?? theme.colors?.gray?.[4];

export const activityBackground = (
  activityCode: ActivityStep | null,
  status: StopStatus
) => {
  if (status === StopStatus.NOT_STARTED) return "bg-white";

  if (status >= StopStatus.FINISHED) return "bg-gray-opacity15";

  switch (activityCode) {
    case ActivityStep.COUPLING:
      return "bg-yellow-opacity15";
    case ActivityStep.LOADING:
      return "bg-green-opacity15";
    case ActivityStep.UNLOADING:
      return "bg-red-opacity15";
    case ActivityStep.DECOUPLING:
      return "bg-gray-opacity15";
    case ActivityStep.REST:
    case ActivityStep.DIVIDER:
      return "bg-blue-opacity15";
    default:
      return "bg-white";
  }
};

export const temperatureColorsToProduct = (temperature: number | null) => {
  if (temperature === null) {
    return { bg: "inherit", c: "inherit" };
  } else if (temperature >= -30 && temperature <= -3) {
    return { bg: "temperature.0", c: "var(--color-white)" };
  } else if (temperature >= -2 && temperature <= 3) {
    return { bg: "temperature.1", c: "inherit" };
  } else if (temperature >= 4 && temperature <= 6) {
    return { bg: "temperature.2", c: "inherit" };
  } else if (temperature >= 7 && temperature <= 12) {
    return { bg: "temperature.3", c: "inherit" };
  } else if (temperature >= 13 && temperature <= 18) {
    return { bg: "temperature.4", c: "inherit" };
  } else if (temperature >= 19 && temperature <= 50) {
    return { bg: "temperature.5", c: "var(--color-white)" };
  } else {
    return { bg: "inherit", c: "inherit" };
  }
};
